import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { Bot, CircleX, Copy, Download, Eye, Filter, History, Info, KeyRound, LogOut, Mail, MapPin, Play, RefreshCw, Send, Settings, Shield, Trash2, Users, X } from "lucide-react";
import type { SessionUser, SettingsView } from "../shared/types.js";
import { api } from "./api.js";
import type { AcquisitionType, AdminUser, AuditLog, CrawlJobReport, CrawlPage, CrawlRegion, CrmExport, DedupeGroup, HelpMessage, Job, JsonObject, JsonValue, Lead, LeadChange, LeadEvent, LeadFilters, LeadList, LeadReviewFeedback, MailAttachment, MailConnectionView, OutreachCampaign, OutreachGuard, OutreachHandoff, OutreachPreviewRow, OutreachSend, OutreachStage, OutreachSuppression, OutreachTemplate, OutreachTemplateForm, PitchSummary, ReviewHandoff, ReviewStage, SettingsTab, Tab } from "./types.js";
import { acquisitionPriorityOptions, acquisitionTypeOptions, emptyLeadFilters, emptyOutreachTemplateForm, leadQualityOptions, outreachStageOptions, reviewStageOptions } from "./types.js";
import { hasUsableLeadEmail, isReviewedAcquisitionCandidate } from "./leadWorkflow.js";
export function JobTable({ jobs, onRefresh, onReport, onMessage }: { jobs: Job[]; onRefresh?: () => void; onReport?: (job: Job) => void; onMessage?: (message: string, tone?: "success" | "error") => void }) {
  return (
    <table>
      <thead><tr><th>Stadt</th><th>Status</th><th>Fortschritt</th><th>Fehler</th><th></th></tr></thead>
      <tbody>{jobs.map((job) => (
        <tr key={job.id}>
          <td>{job.city}<small>{job.radiusMeters}m</small></td>
          <td>{job.status}</td>
          <td>{job.totalCrawled}/{job.totalFound}<small>{job.totalFailed ? `${job.totalFailed} fehlgeschlagen` : ""}</small></td>
          <td>{job.error ?? ""}</td>
          <td className="row-actions">
            {onReport && <button className="icon" onClick={() => onReport(job)} title="Report öffnen"><Eye size={16} /></button>}
            {onRefresh && ["queued", "running"].includes(job.status) ? <button className="danger" onClick={() => cancelCrawlJob(job, onRefresh, onMessage)} title="Job abbrechen"><CircleX size={16} /> Abbrechen</button> : null}
          </td>
        </tr>
      ))}</tbody>
    </table>
  );
}
export function cancelCrawlJob(job: Job, onRefresh?: () => void, onMessage?: (message: string, tone?: "success" | "error") => void) {
  if (!window.confirm(`Crawl #${job.id} "${job.city}" wirklich abbrechen?`)) return;
  api.post<{ cancelled: boolean; status: string }>(`/api/crawl-jobs/${job.id}/cancel`)
    .then((result) => {
      onRefresh?.();
      onMessage?.(result.cancelled ? `Crawl #${job.id} wurde abgebrochen.` : `Job konnte nicht abgebrochen werden, aktueller Status: ${result.status}`, result.cancelled ? "success" : "error");
    })
    .catch((error) => onMessage?.(error.message, "error"));
}
export function ChangeTable({ changes, compact = false }: { changes: LeadChange[]; compact?: boolean }) {
  if (changes.length === 0) return <p className="muted">Keine Änderungen gespeichert.</p>;
  return (
    <table className={compact ? "compact-table" : ""}>
      <thead><tr><th>Zeit</th><th>Typ</th><th>Feld</th><th>Alt</th><th>Neu</th></tr></thead>
      <tbody>{changes.map((change) => (
        <tr key={change.id}>
          <td>{formatDate(change.createdAt)}<small>{change.jobId ? `Job #${change.jobId}` : ""}</small></td>
          <td>{change.changeType}</td>
          <td>{change.fieldName}</td>
          <td>{formatReportValue(change.oldValue)}</td>
          <td>{formatReportValue(change.newValue)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}
export function PanelTitle({ title, subtitle, action }: { title: string; subtitle: string; action?: React.ReactNode }) {
  return <header className="panel-title"><div><h1>{title}</h1><p>{subtitle}</p></div>{action}</header>;
}
export function Metric({ label, value }: { label: string; value: number }) {
  return <section className="metric"><span>{label}</span><strong>{value}</strong></section>;
}
export function ReportTable({ title, rows, columns }: { title: string; rows: Array<Record<string, JsonValue>>; columns: Array<[string, string]> }) {
  return (
    <section className="panel report-panel">
      <h2>{title}</h2>
      <table>
        <thead><tr>{columns.map(([, label]) => <th key={label}>{label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => (
          <tr key={index}>{columns.map(([key]) => <td key={key}>{formatReportValue(row[key])}</td>)}</tr>
        ))}</tbody>
      </table>
    </section>
  );
}
export function queryFromFilters(filters: JsonObject) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== "" && value !== false && value !== null && value !== undefined) params.set(key, String(value));
  });
  return params.toString();
}
export function SettingsGroup({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <section className="panel settings-group">
      <div className="settings-group-head">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <div className="settings-grid">{children}</div>
    </section>
  );
}
export function goLiveLabel(key: string) {
  const labels: Record<string, string> = {
    httpsDomain: "Domain + HTTPS",
    trustProxy: "Trust Proxy aktiv",
    googlePlacesConfigured: "Google Places Key",
    microsoftMailConfigured: "Microsoft Credentials gesetzt",
    outreachMailConfigured: "Gewählter Mailversand eingerichtet",
    safeUnsubscribeAction: "Sichere Abmeldeaktion",
    trackASendable: "Sicherer Erstkontakt sendbar",
    trackBSafelyDisabled: "Direkt-Pitch sicher deaktiviert",
    pilotCrawlCompleted: "Pilot-Crawl abgeschlossen",
    manualReviewSampleDone: "20+ Leads manuell geprüft",
    feedbackLoopActive: "Feedback-Loop aktiv",
    backupScriptsPresent: "Backup-Skripte vorhanden",
    ciConfigured: "CI konfiguriert"
  };
  return labels[key] ?? key;
}
export function Field({
  label,
  value,
  onChange,
  type = "text",
  action,
  status,
  help,
  hint,
  readOnly = false,
  autoComplete,
  required = false,
  minLength
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  action?: React.ReactNode;
  status?: React.ReactNode;
  help?: string;
  hint?: string;
  readOnly?: boolean;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
}) {
  return (
    <label>
      <span className="field-label-row">
        <span>{label}</span>
      </span>
      <div className={action ? "field-with-action" : undefined}>
        <input type={type} value={value} readOnly={readOnly} autoComplete={autoComplete} required={required} minLength={minLength} onChange={(e) => onChange(e.target.value)} />
        {action}
      </div>
      {hint && <small className="field-hint">{hint}</small>}
      {help && <ApiHelp text={help} />}
      {status}
    </label>
  );
}
export function ApiHelp({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <details className="api-help">
      <summary><Info size={14} /> Berechtigungen anzeigen</summary>
      <div className="api-help-content">
        <p>{text}</p>
        <button type="button" onClick={copyText}><Copy size={14} /> {copied ? "Kopiert" : "Text kopieren"}</button>
      </div>
    </details>
  );
}
export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="toggle"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /><span>{label}</span></label>;
}
export function Priority({ value }: { value: number }) {
  return <span className="priority" style={{ borderColor: priorityColor(value), color: priorityColor(value) }}>{priorityLabel(value)}</span>;
}
export function CategoryTags({ cuisine, categories }: { cuisine: string | null; categories: string[] }) {
  const labels = formatCategoryLabels(cuisine, categories);
  if (labels.length === 0) return <>-</>;
  return <span className="category-tags">{labels.map((label) => <span key={label}>{label}</span>)}</span>;
}
export function LeadHealthBadge({ lead }: { lead: Lead }) {
  const health = leadHealth(lead);
  return <span className={`health-badge ${health.level}`} title={health.reasons.join(" · ")}>{health.label}</span>;
}
export function LeadQualityFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label>
      <span>Lead-Qualität</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">alle</option>
        {leadQualityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}
export function ContactStatusSelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label>
      <span>Kontaktstatus</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">alle</option>
        <option value="complete">komplett</option>
        <option value="missing_email">Mail fehlt</option>
        <option value="missing_director">GF fehlt</option>
        <option value="missing_both">Mail + GF fehlen</option>
      </select>
    </label>
  );
}
export function ReviewStageSelect({ value, onChange, includeAll = false }: { value: string; onChange: (value: string) => void; includeAll?: boolean }) {
  return (
    <label>
      <span>Review-Status</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {includeAll && <option value="">alle</option>}
        {reviewStageOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label} - {option.description}</option>
        ))}
      </select>
    </label>
  );
}
export function AcquisitionTypeSelect({ value, onChange, includeAll = false }: { value: string; onChange: (value: string) => void; includeAll?: boolean }) {
  return (
    <label>
      <span>Akquise-Einordnung</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {includeAll && <option value="">alle</option>}
        {acquisitionTypeOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label} - {option.description}</option>
        ))}
      </select>
    </label>
  );
}
export function AcquisitionPrioritySelect({ value, onChange, includeAll = false }: { value: string; onChange: (value: string) => void; includeAll?: boolean }) {
  return (
    <label>
      <span>Akquise-Priorität</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {includeAll && <option value="">alle</option>}
        {acquisitionPriorityOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}
export function OutreachStageSelect({ value, onChange, includeAll = false }: { value: string; onChange: (value: string) => void; includeAll?: boolean }) {
  return (
    <label>
      <span>Outreach-Status</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {includeAll && <option value="">alle</option>}
        {outreachStageOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label} - {option.description}</option>
        ))}
      </select>
    </label>
  );
}
export function ReviewStatusLegend({ compact = false }: { compact?: boolean }) {
  return (
    <section className={compact ? "status-legend compact" : "status-legend"}>
      <strong>Review-Status ist der manuelle Arbeitsstatus, nicht der Crawl-Status.</strong>
      <div>
        {reviewStageOptions.map((option) => (
          <span key={option.value}><b>{option.label}</b>: {option.description}</span>
        ))}
      </div>
      <small>Gereviewte Leads werden nach YellowTime-Anwendungsfall eingeordnet: mobile Zeiterfassung, Einsatz-/Objektkoordination oder digitale Arbeitsnachweise. Der Kontaktstatus bleibt separat.</small>
    </section>
  );
}
export function formatLeadQuality(value?: string | null) {
  return leadQualityOptions.find((option) => option.value === value)?.label ?? "Unklar";
}
export function reviewStageFromLead(lead: Lead): ReviewStage {
  if (isBlockedLead(lead)) return "blocked";
  if (lead.reviewStatus === "reviewed" || lead.reviewStatus === "qualified" || lead.reviewStatus === "rejected" || (lead.acquisitionType && lead.acquisitionType !== "unassigned")) return "reviewed";
  return "new";
}
export function formatLeadReviewStage(lead: Lead) {
  return reviewStageOptions.find((option) => option.value === reviewStageFromLead(lead))?.label ?? "Neu";
}
export function formatAcquisitionType(value?: string | null) {
  return acquisitionTypeOptions.find((option) => option.value === value)?.label ?? "Offen";
}
export function formatAcquisitionPriority(value?: string | number | null) {
  if (!value) return "-";
  return acquisitionPriorityOptions.find((option) => option.value === String(value))?.label ?? String(value);
}
export function formatOutreachStage(value?: string | null) {
  return outreachStageOptions.find((option) => option.value === value)?.label ?? "Offen";
}
export function formatOutreachTrack(value?: string | null) {
  return value === "b" ? "Direkt-Pitch (streng)" : "Sicherer Erstkontakt";
}
export function formatAcquisitionSummary(lead: Lead) {
  const type = formatAcquisitionType(lead.acquisitionType);
  const priority = lead.acquisitionPriority ? ` · ${formatAcquisitionPriority(lead.acquisitionPriority)}` : "";
  return `${formatLeadReviewStage(lead)} · ${type}${priority}`;
}
export function suggestedTemplateForLeads(templates: OutreachTemplate[], leads: Lead[]) {
  if (templates.length === 0 || leads.length === 0) return null;
  const types = new Set(leads.map((lead) => lead.acquisitionType).filter(Boolean));
  const targetName = types.size === 1 && types.has("automation")
    ? "Akquise – Mobile Zeiterfassung"
    : types.size === 1 && types.has("distress")
      ? "Akquise – Einsatz- & Objektkoordination"
      : "Akquise – Arbeitsnachweise & Digitalisierung";
  return templates.find((template) => template.name === targetName)
    ?? templates.find((template) => template.name.toLowerCase().includes("alles"))
    ?? templates[0]
    ?? null;
}
export function isAcquisitionCandidate(value?: string | null) {
  return value === "automation" || value === "distress" || value === "transformation";
}
export function isBlockedLead(lead: Lead) {
  return lead.acquisitionType === "blocked" || lead.reviewStatus === "customer" || lead.priority === 99 || lead.leadQuality === "chain" || lead.leadQuality === "hotel" || lead.leadQuality === "non_company";
}
export function isLeadOutreachCandidate(lead: Lead) {
  return isReviewedAcquisitionCandidate(lead) && !lead.outreachOptOut && !isBlockedLead(lead);
}
export function isLeadOutreachSelectable(lead: Lead) {
  return isLeadOutreachCandidate(lead) && hasUsableLeadEmail(lead);
}
export function isContactedForHubSpot(lead: Lead) {
  return lead.outreachStage === "acquisition_email_sent" || lead.outreachStage === "called" || lead.outreachStage === "not_interested" || lead.outreachStage === "lead";
}
export function summarizeReviewBreakdown(reviewBreakdown: Array<{ reviewStatus: string; total: number }>, acquisitionBreakdown: Array<{ acquisitionType: string; total: number }>) {
  const total = (status: string) => reviewBreakdown.find((row) => row.reviewStatus === status)?.total ?? 0;
  const blocked = (acquisitionBreakdown.find((row) => row.acquisitionType === "blocked")?.total ?? 0) + total("customer");
  const reviewed = total("reviewed") + total("qualified") + total("rejected");
  return [
    { reviewStage: "Neu", total: total("new") + total("audit") },
    { reviewStage: "Gereviewed", total: Math.max(0, reviewed - blocked) },
    { reviewStage: "Gesperrt", total: blocked }
  ];
}
export function readAttachmentFile(file: File): Promise<MailAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const contentBase64 = result.includes(",") ? result.split(",").pop() ?? "" : result;
      resolve({ name: file.name, contentType: file.type || "application/octet-stream", contentBase64 });
    };
    reader.onerror = () => reject(reader.error ?? new Error("Anhang konnte nicht gelesen werden"));
    reader.readAsDataURL(file);
  });
}
export function priorityColor(value: number) {
  if (value === 1) return "#b42318";
  if (value === 2) return "#c06b00";
  if (value === 3) return "#2563eb";
  if (value === 99) return "#667085";
  return "#087443";
}
export function priorityLabel(value: number) {
  if (value === 1) return "1 Hohe Chance";
  if (value === 2) return "2 Gute Chance";
  if (value === 3) return "3 Beobachten";
  if (value === 99) return "Gesperrt";
  return "4 niedrig";
}
export function formatDate(value: string) {
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}
export function compactJson(value: JsonObject) {
  const text = JSON.stringify(value);
  return text.length > 180 ? `${text.slice(0, 177)}...` : text;
}
export function formatList(values: string[]) {
  const unique = [...new Set(values.map((value) => value.trim()).filter(Boolean))];
  return unique.length ? unique.join(", ") : "-";
}
export function formatCategoryLabels(cuisine: string | null, categories: string[]) {
  const generic = new Set(["establishment", "food", "point_of_interest", "store"]);
  const labels = [
    cuisine,
    ...categories
      .filter((value) => !generic.has(value))
      .map((value) => categoryLabel(value))
      .filter(Boolean)
  ].filter(Boolean) as string[];
  return [...new Set(labels.map((value) => value.trim()).filter(Boolean))].slice(0, 8);
}
export function formatEmailQuality(lead: Pick<Lead, "email">) {
  const quality = clientEmailQuality(lead.email);
  if (!quality.usable) return `${lead.email ?? "-"} · keine nutzbare Mail`;
  return `${quality.email} · ${quality.kind === "generic" ? "generisch" : "direkt"}`;
}
export function leadHealth(lead: Lead) {
  const reasons: string[] = [];
  if (isBlockedLead(lead)) reasons.push("gesperrt oder kein passender Company-Lead");
  if (!isAcquisitionCandidate(lead.acquisitionType)) reasons.push("kein Akquise-Kandidat freigegeben");
  if (!clientEmailQuality(lead.email).usable) reasons.push("keine nutzbare E-Mail");
  if (isCrawlerBlockedLead(lead)) reasons.push("Website blockiert Crawler");
  if (lead.leadQuality === "review" || lead.leadQuality === "unclear") reasons.push("Lead-Qualität prüfen");
  if (!lead.managingDirector) reasons.push("Geschäftsführer fehlt");
  if (lead.email && /^(info|kontakt|mail|office)@/i.test(lead.email)) reasons.push("generische Mailbox");
  if (isBlockedLead(lead) || !isAcquisitionCandidate(lead.acquisitionType) || !clientEmailQuality(lead.email).usable) {
    return { level: "risk", label: "kritisch", reasons };
  }
  if (reasons.length > 0) return { level: "review", label: "prüfen", reasons };
  return { level: "good", label: "sicher", reasons: ["freigegeben, kontaktierbar, gute Datenlage"] };
}
export function isCrawlerBlockedLead(lead: Pick<Lead, "evidence">) {
  return hasEvidence(lead, "web", "crawler-blocked");
}
export function formatContactStatus(lead: Pick<Lead, "contactStatus" | "managingDirector" | "email">) {
  const status = lead.contactStatus ?? deriveContactStatus(lead);
  if (status === "complete") return `GF: ${lead.managingDirector}`;
  return formatContactStatusLabel(status);
}
export function formatContactStatusLabel(status: string | null | undefined) {
  if (status === "complete") return "komplett";
  if (status === "missing_email") return "Mail fehlt";
  if (status === "missing_director") return "GF fehlt";
  if (status === "missing_both") return "Mail + GF fehlen";
  return "-";
}
export function deriveContactStatus(lead: Pick<Lead, "managingDirector" | "email">) {
  const hasMail = clientEmailQuality(lead.email).usable;
  const hasDirector = Boolean(lead.managingDirector);
  if (hasMail && hasDirector) return "complete";
  if (!hasMail && !hasDirector) return "missing_both";
  if (!hasMail) return "missing_email";
  return "missing_director";
}
export function clientEmailQuality(email: string | null | undefined) {
  const normalized = email?.trim().toLowerCase();
  if (!normalized || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) return { usable: false as const, email: normalized ?? null, kind: "invalid" };
  const [localPart, domain] = normalized.split("@");
  const unusableExact = new Set(["info@example.com", "test@example.com", "your@email.com", "your@email.de", "ihre@email.de"]);
  const unusableDomains = new Set(["example.com", "example.org", "example.net", "sentry-next.wixpress.com"]);
  const unusableLocals = new Set(["your", "ihre", "example", "email", "test", "name", "user"]);
  const noReply = /^(no-?reply|do-?not-?reply|donotreply)$/i.test(localPart);
  const technical = /^[a-f0-9]{24,}$/i.test(localPart) && /(wix|sentry|sandbox|test)/i.test(domain);
  if (unusableExact.has(normalized) || unusableDomains.has(domain) || unusableLocals.has(localPart) || noReply || technical) {
    return { usable: false as const, email: normalized, kind: "unusable" };
  }
  return { usable: true as const, email: normalized, kind: /^(info|kontakt|mail|office)@/i.test(normalized) ? "generic" : "direct" };
}
export function StatusWithUrl({ label, url, muted = false }: { label: string; url?: string | null; muted?: boolean }) {
  return <span className={muted ? "muted-status" : undefined}>{label}{url ? <> · <a href={url} target="_blank" rel="noreferrer">Quelle</a></> : null}</span>;
}
export function hasEvidence(lead: Pick<Lead, "evidence">, key: "reservation" | "delivery" | "web", marker: string) {
  const value = lead.evidence?.[key];
  return Array.isArray(value) && value.some((item) => String(item).includes(marker));
}
export function categoryLabel(value: string) {
  const labels: Record<string, string> = {
    restaurant: "Restaurant",
    meal_takeaway: "Take-away",
    meal_delivery: "Lieferung",
    cafe: "Cafe",
    bar: "Bar",
    bakery: "Bäckerei",
    night_club: "Nachtgastronomie",
    lodging: "Hotel",
    tourist_attraction: "Touristisch"
  };
  return labels[value] ?? value.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}
export function formatOpeningHours(value: JsonObject | null) {
  if (!value) return "-";
  const weekdayText = value.weekdayText ?? value.weekday_text ?? value.openingHours;
  if (Array.isArray(weekdayText)) return weekdayText.map(String).join("\n");
  if (typeof weekdayText === "string") return weekdayText;
  const text = JSON.stringify(value);
  return text.length > 260 ? `${text.slice(0, 257)}...` : text;
}
export function prettyJson(value: JsonValue) {
  return JSON.stringify(value, null, 2);
}
export function formatReportValue(value: JsonValue) {
  if (typeof value === "number") return Number.isInteger(value) ? value : value.toFixed(1);
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}
export function FullScreen({ title }: { title: string }) {
  return <main className="auth-screen"><h1>{title}</h1></main>;
}
